import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { useHousehold, useHouseholdId } from "@/app/useHousehold";
import { useMediaQuery } from "@/app/useMediaQuery";
import { LanguagePicker } from "@/i18n/LanguagePicker";
import { useT } from "@/i18n/LocaleProvider";
import type { MessageKey } from "@/i18n/catalog";
import { ThemePicker } from "@/theme/ThemePicker";
import { buttonClass, secondaryButtonClass } from "./ui";

/**
 * Where everything is, grouped by what somebody is doing: seeing where they
 * stand, handling money that has moved, planning money that has not, and
 * looking after the household's setup. Order inside a group is order of use.
 */
const NAV: ReadonlyArray<{ group: MessageKey; items: ReadonlyArray<[string, MessageKey]> }> = [
  {
    group: "nav.group.overview",
    items: [
      ["dashboard", "nav.dashboard"],
      ["reports", "nav.report"],
    ],
  },
  {
    group: "nav.group.money",
    items: [
      ["transactions", "nav.transactions"],
      ["accounts", "nav.accounts"],
      ["upcoming", "nav.upcoming"],
      ["import", "nav.import"],
    ],
  },
  {
    group: "nav.group.plan",
    items: [
      ["budgets", "nav.budgets"],
      ["goals", "nav.goals"],
    ],
  },
  {
    group: "nav.group.manage",
    items: [
      ["categories", "nav.categories"],
      ["history", "nav.history"],
      ["data", "nav.data"],
      ["manual", "nav.manual"],
    ],
  },
];

function PlusIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className="h-4 w-4">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function Wordmark({ household }: { household: string | undefined }) {
  const t = useT();
  return (
    <div className="min-w-0">
      <span className="block text-lg font-semibold tracking-tight text-ink">Kontora</span>
      <span className="block truncate text-xs text-muted">{household ?? t("app.tagline")}</span>
    </div>
  );
}

/** The theme picker and the account actions, shared by both layouts. */
function SettingsPanel({ showLanguage }: { showLanguage: boolean }) {
  const t = useT();
  const { logout, user } = useAuth();
  const who = user?.profile.email ?? user?.profile.sub ?? "";
  return (
    <div className="space-y-5">
      <ThemePicker />
      {showLanguage && <LanguagePicker />}
      <div className="border-t border-line pt-4">
        {who && (
          <p className="mb-3 truncate text-xs text-muted">{t("app.signedInAs", { who })}</p>
        )}
        <button
          type="button"
          onClick={() => void logout()}
          className={`${secondaryButtonClass} min-h-11 w-full`}
        >
          {t("app.signOut")}
        </button>
      </div>
    </div>
  );
}

/**
 * A button that opens the settings panel, closing it again on Escape, on a
 * click elsewhere, and when the route changes.
 */
function SettingsDisclosure({
  placement,
  showLanguage,
  children,
}: {
  placement: "above" | "below";
  showLanguage: boolean;
  children: ReactNode;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const root = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointer = (event: PointerEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={open ? t("app.closeSettings") : t("app.openSettings")}
        onClick={() => setOpen((value) => !value)}
        className={`${secondaryButtonClass} min-h-11 ${placement === "above" ? "w-full justify-between" : "px-3"}`}
      >
        {children}
      </button>
      {open && (
        <div
          id={panelId}
          className={`absolute z-30 w-72 max-w-[calc(100vw-2rem)] rounded-panel border border-line bg-surface p-4 shadow-lg ${
            placement === "above" ? "bottom-full left-0 mb-2" : "right-0 top-full mt-2"
          }`}
        >
          <SettingsPanel showLanguage={showLanguage} />
        </div>
      )}
    </div>
  );
}

function GearIcon() {
  // Two sliders: "adjust how this looks", which is what is behind the button.
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4">
      <path
        d="M3 6h9M16 6h1M3 14h2M9 14h8"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="14" cy="6" r="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="7" cy="14" r="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function SidebarLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex min-h-10 items-center rounded-control px-3 text-sm transition-colors ${
          isActive
            ? "bg-accent-soft font-medium text-accent-text"
            : "text-muted hover:bg-sunken hover:text-ink"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function StripLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        // A 44px-tall target, because these tabs are how a phone reaches the
        // entry form (project.md §5.2).
        `inline-flex min-h-11 shrink-0 items-center whitespace-nowrap border-b-2 text-sm transition-colors ${
          isActive
            ? "border-accent font-medium text-accent-text"
            : "border-transparent text-muted hover:text-ink"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const t = useT();
  const { isAuthenticated } = useAuth();
  const householdId = useHouseholdId();
  const household = useHousehold();
  const wide = useMediaQuery("(min-width: 1024px)");

  // Navigation appears only once there is a household to navigate within. A
  // menu over an onboarding screen would offer destinations that do not exist.
  const inHousehold = isAuthenticated && Boolean(householdId && household);
  const base = `/households/${householdId ?? ""}`;
  const newTransaction = `${base}/transactions/new`;

  if (wide && inHousehold) {
    return (
      <div className="min-h-screen lg:grid lg:grid-cols-[15.5rem_1fr]">
        <aside className="sticky top-0 flex h-screen flex-col border-r border-line bg-surface">
          <header className="px-5 pb-4 pt-6">
            <Wordmark household={household?.name} />
            <NavLink to={newTransaction} className={`${buttonClass} mt-5 min-h-11 w-full`}>
              <PlusIcon />
              {t("transactions.add")}
            </NavLink>
          </header>
          <nav aria-label={t("nav.main")} className="flex-1 overflow-y-auto px-3 pb-4">
            {NAV.map(({ group, items }) => (
              <div key={group} className="mt-4 first:mt-1">
                <p className="px-3 pb-1 text-xs font-medium text-muted">{t(group)}</p>
                <ul className="ml-3 space-y-0.5">
                  {items.map(([path, label]) => (
                    <li key={path}>
                      <SidebarLink to={`${base}/${path}`}>{t(label)}</SidebarLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
          <div className="space-y-3 border-t border-line p-4">
            <LanguagePicker />
            <SettingsDisclosure placement="above" showLanguage={false}>
              <span className="flex items-center gap-2">
                <GearIcon />
                {t("app.settings")}
              </span>
              <span
                aria-hidden="true"
                className="h-3 w-3 rounded-full bg-accent ring-2 ring-accent-soft"
              />
            </SettingsDisclosure>
          </div>
        </aside>
        <main className="min-w-0 px-6 py-10 xl:px-12">
          <div className="mx-auto max-w-page">{children}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-20 border-b border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-page items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Wordmark household={household?.name} />
          {isAuthenticated && (
            <div className="flex shrink-0 items-center gap-2">
              {inHousehold && (
                <NavLink
                  to={newTransaction}
                  aria-label={t("transactions.add")}
                  className={`${buttonClass} min-h-11 min-w-11 px-3`}
                >
                  <PlusIcon />
                </NavLink>
              )}
              <SettingsDisclosure placement="below" showLanguage>
                <GearIcon />
              </SettingsDisclosure>
            </div>
          )}
        </div>
        {inHousehold && (
          // Every destination on a phone, in one strip that scrolls inside its
          // own container rather than pushing the page sideways. K-15's speed
          // gate measures this at 375px.
          <nav
            aria-label={t("nav.main")}
            className="mx-auto flex max-w-page gap-5 overflow-x-auto px-4 sm:px-6"
          >
            {NAV.flatMap(({ items }) => items).map(([path, label]) => (
              <StripLink key={path} to={`${base}/${path}`}>
                {t(label)}
              </StripLink>
            ))}
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-page px-4 py-6 sm:px-6 sm:py-10">{children}</main>
    </div>
  );
}
